import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queue as toastQueue } from "@/components/Toast";
import { useAddClientComment, useClientCommentOp } from "../api/useClientPortal";
import { useAddClientBriefComment } from "../api/useClientBriefs";
import type { PortalSource } from "../api/clientPortalApi";
import {
  groupThreads,
  type CommentEntry,
  type CommentSource,
  type CommentThreadData,
} from "@/app/routes/dashboard/content-calendar/comments/commentModel";
import { clientThreads, partLabel, type ClientPost } from "../clientReviewModel";

/** The part of the page a comment is on, as the API names it ("hook", "shot" + "3"). */
export interface ClientCommentPart {
  part: string;
  key?: string | null;
  /** How the composer and the rail say it: "the hook", "shot 03". */
  label: string;
}

/** A comment written on this page: on screen at once, kept with Try again if it fails. */
type LocalPost = {
  localId: string;
  slotId: string;
  source: CommentSource;
  /** The concept version id or the brief id it goes to. */
  targetId: string;
  version: number;
  body: string;
  createdAt: string;
  state: "sending" | "failed";
  /** A new thread's part; none for the whole post. */
  anchor?: ClientCommentPart | null;
  /** A reply: the thread it goes under (its id here and its root comment's id). */
  parent?: { threadId: string; commentId: string } | null;
};

/** The clock comment times read against; a minute is fine enough. */
function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

let localSeq = 0;

function localEntry(entry: LocalPost, authorName: string): CommentEntry {
  return {
    id: entry.localId,
    authorType: "CLIENT",
    authorName,
    body: entry.body,
    createdAt: entry.createdAt,
    sendState: entry.state,
  };
}

/** The root comment's id behind a thread id ("concept:abc" is abc). */
function rootId(thread: CommentThreadData): string {
  return thread.comment.id;
}

/**
 * The client's comments on one post: every shared thread the portal returns
 * (with its part, its replies and whether it is done), plus what this page is
 * posting. A new comment, a reply and Mark done all show at once; a post that
 * fails stays with Try again, and a done that fails goes back with a toast.
 */
export function useClientComments({
  post,
  source,
  authorName,
  targets,
}: {
  post: ClientPost | null;
  source: PortalSource;
  authorName: string;
  /** Where each part's comment goes right now; null when that part is closed. */
  targets: Record<CommentSource, { id: string; version: number } | null>;
}) {
  const qc = useQueryClient();
  const addConcept = useAddClientComment(source, { optimistic: false });
  const addBrief = useAddClientBriefComment(source, { optimistic: false });
  const commentOp = useClientCommentOp(source);
  const [pending, setPending] = useState<LocalPost[]>([]);
  // Done or open again, shown before the month's next read says so.
  const [doneOverride, setDoneOverride] = useState<Record<string, string | null>>({});
  const now = useMinuteClock();
  const slotId = post?.slot.id ?? null;

  const threads: CommentThreadData[] = useMemo(() => {
    if (!post) return [];
    const mine = pending.filter((entry) => entry.slotId === post.slot.id);
    const server = clientThreads(post).map((thread) => {
      const replies = mine.filter((entry) => entry.parent?.threadId === thread.id);
      const override = doneOverride[thread.id];
      return {
        ...thread,
        ...(replies.length ? { replies: [...(thread.replies ?? []), ...replies.map((entry) => localEntry(entry, authorName))] } : {}),
        ...(override !== undefined ? { resolvedAt: override } : {}),
      };
    });
    const local: CommentThreadData[] = mine
      .filter((entry) => !entry.parent)
      .map((entry) => ({
        id: `local:${entry.localId}`,
        source: entry.source,
        version: entry.version,
        comment: localEntry(entry, authorName),
        ...(entry.anchor ? { anchor: { part: entry.anchor.part, label: entry.anchor.label } } : {}),
      }));
    return [...server, ...local];
  }, [post, pending, authorName, doneOverride]);

  const groups = useMemo(() => groupThreads(threads), [threads]);

  const setState = (localId: string, state: LocalPost["state"] | null) =>
    setPending((list) =>
      state === null
        ? list.filter((entry) => entry.localId !== localId)
        : list.map((entry) => (entry.localId === localId ? { ...entry, state } : entry)),
    );

  const send = async (entry: LocalPost) => {
    setState(entry.localId, "sending");
    const input = {
      body: entry.body,
      anchor: entry.anchor ? { part: entry.anchor.part, key: entry.anchor.key ?? null } : null,
      parentId: entry.parent?.commentId ?? null,
    };
    try {
      if (entry.source === "concept") {
        await addConcept.mutateAsync({ versionId: entry.targetId, ...input });
        // Held until the thread has the server's copy, so it never blinks out.
        await qc.invalidateQueries({ queryKey: ["clientPortal", "month", source.id] });
      } else {
        await addBrief.mutateAsync({ briefId: entry.targetId, ...input });
        await qc.invalidateQueries({ queryKey: ["clientPortal", "briefs", source.id] });
      }
      setState(entry.localId, null);
    } catch {
      setState(entry.localId, "failed");
    }
  };

  const queuePost = (entry: Omit<LocalPost, "localId" | "slotId" | "createdAt" | "state">) => {
    if (!slotId) return;
    localSeq += 1;
    const next: LocalPost = {
      ...entry,
      localId: `${Date.now().toString(36)}-${localSeq}`,
      slotId,
      createdAt: new Date().toISOString(),
      state: "sending",
    };
    setPending((list) => [...list, next]);
    void send(next);
  };

  /** A new thread on the open tab, on a part of the page or on the whole post. */
  const postComment = (part: CommentSource, body: string, anchor: ClientCommentPart | null = null) => {
    const target = targets[part];
    if (!target) return;
    queuePost({ source: part, targetId: target.id, version: target.version, body, anchor });
  };

  /** A reply goes to the version its thread was written on, while that part takes comments. */
  const reply = (threadId: string, body: string) => {
    const thread = threads.find((entry) => entry.id === threadId);
    if (!thread || thread.id.startsWith("local:")) return;
    const target = targets[thread.source];
    if (!target || !post) return;
    // A reply sits on its thread's version: the concept version it was written on, or the brief.
    const targetId =
      thread.source === "concept"
        ? (post.slot.conceptVersions?.find((version) => version.version === thread.version)?.id ?? target.id)
        : target.id;
    queuePost({
      source: thread.source,
      targetId,
      version: thread.version,
      body,
      parent: { threadId, commentId: rootId(thread) },
    });
  };

  const retry = (threadId: string) => {
    const entry = pending.find((item) => `local:${item.localId}` === threadId);
    if (entry) void send(entry);
  };

  const toggleDone = async (threadId: string) => {
    const thread = threads.find((entry) => entry.id === threadId);
    if (!thread || thread.id.startsWith("local:")) return;
    const done = !!thread.resolvedAt;
    setDoneOverride((all) => ({ ...all, [threadId]: done ? null : new Date().toISOString() }));
    const clear = () =>
      setDoneOverride((all) => {
        const next = { ...all };
        delete next[threadId];
        return next;
      });
    try {
      await commentOp.mutateAsync({ surface: thread.source, commentId: rootId(thread), op: done ? "reopen" : "resolve" });
      await qc.invalidateQueries({
        queryKey: ["clientPortal", thread.source === "concept" ? "month" : "briefs", source.id],
      });
      clear();
    } catch {
      clear();
      toastQueue.add({
        title: done ? "Not opened again" : "Not marked done",
        description: "It did not go through. Please try again.",
        variant: "error",
      });
    }
  };

  /** Can this thread take a reply right now: its part takes comments. */
  const canReply = (thread: CommentThreadData) => !thread.id.startsWith("local:") && !!targets[thread.source];

  return {
    threads,
    open: groups.open,
    done: groups.done,
    count: groups.open.length,
    now,
    post: postComment,
    reply,
    retry,
    toggleDone: (threadId: string) => void toggleDone(threadId),
    canReply,
  };
}

export type ClientCommentsData = ReturnType<typeof useClientComments>;

/** A thread's marker on the page: its part, its number, and the label the rail uses. */
export interface ClientMarker {
  threadId: string;
  part: string;
  key: string | null;
  number: number;
}

/** The open threads pinned to a part on the version and tab on screen, for the page's markers. */
export function markersOf(threads: readonly CommentThreadData[], source: CommentSource, version: number): ClientMarker[] {
  const out: ClientMarker[] = [];
  for (const thread of threads) {
    const anchor = thread.anchor as (CommentThreadData["anchor"] & { key?: string | null }) | null | undefined;
    if (!anchor?.number || thread.source !== source || thread.version !== version || thread.resolvedAt) continue;
    out.push({ threadId: thread.id, part: anchor.part, key: anchor.key ?? null, number: anchor.number });
  }
  return out;
}

export { partLabel };
